import { CustomProvider, initializeAppCheck } from "firebase/app-check";

import { configuration } from "./configuration";

import type { FirebaseApp } from "firebase/app";
import type { AppCheckToken } from "firebase/app-check";
import type { TurnstileApi, WindowWithTurnstile } from "./appCheck.types";

const TURNSTILE_SCRIPT_ID = "tayan-admin-turnstile";
const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstilePromise: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  const current = (window as WindowWithTurnstile).turnstile;
  if (current) return Promise.resolve(current);
  if (turnstilePromise) return turnstilePromise;

  turnstilePromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    const loaded = () => {
      const api = (window as WindowWithTurnstile).turnstile;
      if (api) resolve(api);
      else reject(new Error("Yönetici oturumu doğrulaması başlatılamadı."));
    };
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", () => {
      turnstilePromise = null;
      reject(new Error("Yönetici oturumu doğrulaması başlatılamadı."));
    }, { once: true });
    if (!existing) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      document.head.appendChild(script);
    }
  });
  return turnstilePromise;
};

const requestChallenge = async (siteKey: string): Promise<string> => {
  const api = await loadTurnstile();
  const container = document.createElement("div");
  container.className = "turnstile-challenge";
  document.body.appendChild(container);
  return new Promise((resolve, reject) => {
    let settled = false;
    let widgetId = "";
    const finish = (result: { token: string } | { error: Error }) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      if (widgetId) api.remove(widgetId);
      container.remove();
      if ("token" in result) resolve(result.token);
      else reject(result.error);
    };
    const timeout = window.setTimeout(
      () => finish({ error: new Error("Yönetici oturumu doğrulaması zaman aşımına uğradı.") }),
      60_000,
    );
    widgetId = api.render(container, {
      action: "tayan_admin",
      appearance: "interaction-only",
      callback: (token) => finish({ token }),
      "error-callback": () => finish({ error: new Error("Yönetici oturumu doğrulanamadı.") }),
      execution: "execute",
      sitekey: siteKey,
      "timeout-callback": () => finish({ error: new Error("Yönetici oturumu doğrulanamadı.") }),
    });
    api.execute(widgetId);
  });
};

const requestAppCheckToken = async (
  identityToken: string,
  siteKey: string,
): Promise<AppCheckToken> => {
  const turnstileToken = await requestChallenge(siteKey);
  const response = await fetch("/v1/app-check/token", {
    body: JSON.stringify({ requestId: crypto.randomUUID(), turnstileToken }),
    cache: "no-store",
    headers: {
      Authorization: `Bearer ${identityToken}`,
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  const body = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (
    !response.ok ||
    !body ||
    typeof body.token !== "string" ||
    typeof body.expireTimeMillis !== "number" ||
    !Number.isSafeInteger(body.expireTimeMillis) ||
    Number(body.expireTimeMillis) <= Date.now()
  ) {
    throw new Error("Yönetici uygulama kanıtı alınamadı.");
  }
  return { token: body.token, expireTimeMillis: Number(body.expireTimeMillis) };
};

export const initializeAdminAppCheck = (
  app: FirebaseApp,
  getIdentityToken: () => Promise<string>,
) => {
  if (!configuration.turnstileSiteKey) throw new Error("Turnstile yapılandırması eksik.");
  const provider = new CustomProvider({
    getToken: async () => ({
      ...await requestAppCheckToken(await getIdentityToken(), configuration.turnstileSiteKey!),
      issuedAtTimeMillis: Date.now(),
    }),
  });
  return initializeAppCheck(app, { isTokenAutoRefreshEnabled: false, provider });
};
